import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import Loading from 'components/Loading';
import ProfileSetupScreen from 'components/ProfileSetupScreen';
import { useAuthStatus, useLogout } from 'features/auth';
import { isPhoneRequired } from 'profile';
import { splitE164, toE164 } from 'utils/phone';
import { findActionIn } from 'actions';
import { useMyProfile } from './useMyProfile';
import { useEditMyProfile } from './useEditMyProfile';
export default function ProfileSetupRoute() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const status = useAuthStatus();
    const logout = useLogout();
    const myProfile = useMyProfile();
    const editMutation = useEditMyProfile();
    const user = status.kind === 'authenticated' ? status.user : null;
    const [fullName, setFullName] = useState('');
    const [whatsappCountryCode, setWhatsappCountryCode] = useState('+82');
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [didSeed, setDidSeed] = useState(false);
    // Pre-fill from the myProfile GET response: name from the subject (displayName fallback
    // when the backend has no name yet), phone split from whatsAppE164.
    useEffect(() => {
        if (didSeed)
            return;
        if (!user)
            return;
        if (!myProfile.data)
            return;
        setFullName(user.name || user.profile.displayName || '');
        const split = splitE164(myProfile.data.object?.whatsAppE164 || '');
        if (split) {
            setWhatsappCountryCode(split.countryCode);
            setWhatsappNumber(split.nationalNumber);
        }
        setDidSeed(true);
    }, [didSeed, myProfile.data, user]);
    if (!user)
        return null;
    if (myProfile.isPending)
        return _jsx(Loading, { isVisible: true, title: "" });
    // Either the GET failed, or it succeeded without an `edit` action — neither path lets the
    // form submit, so render an inline error state with retry + sign-out instead of redirecting
    // (a redirect to /dashboard would loop because the route guard still considers setup needed).
    const editAvailable = Boolean(findActionIn(myProfile.data?.object?.potentialAction, 'edit'));
    const cannotProceed = myProfile.isError || !myProfile.data || !editAvailable;
    if (cannotProceed) {
        return (_jsx("div", { className: "flex min-h-screen items-center justify-center bg-[#f9fafb] px-4 py-8 text-[#0a0a0a]", children: _jsxs("div", { className: "flex max-w-160 flex-col items-center gap-4 border-2 border-[#fecaca] bg-white p-8.5 text-center", children: [_jsx("h2", { className: "m-0 text-[24px] font-semibold leading-8", children: t('profile_setup_unavailable_title') }), _jsx("p", { className: "m-0 text-[16px] leading-6 text-[#4a5565]", children: t('profile_setup_unavailable_body') }), _jsxs("div", { className: "flex flex-wrap justify-center gap-3", children: [_jsx("button", { onClick: () => void myProfile.refetch(), disabled: myProfile.isFetching, className: "inline-flex min-h-10.5 items-center justify-center border border-[#3562e8] bg-[#3562e8] px-4 text-[16px] leading-6 text-white disabled:opacity-50", type: "button", children: t('profile_setup_retry') }), _jsx("button", { onClick: () => void logout.mutateAsync(), disabled: logout.isPending, className: "inline-flex min-h-10.5 items-center justify-center border border-[#99a1af] bg-white px-4 text-[16px] leading-6 text-[#0a0a0a] disabled:opacity-50", type: "button", children: t('auth_sign_out') })] })] }) }));
    }
    const phoneRequired = isPhoneRequired(user);
    const onComplete = () => {
        const trimmedName = fullName.trim();
        const trimmedNumber = whatsappNumber.trim();
        const trimmedCode = whatsappCountryCode.trim();
        if (!trimmedName)
            return;
        let whatsAppE164;
        if (trimmedNumber) {
            const formatted = toE164(trimmedCode, trimmedNumber);
            if (!formatted)
                return;
            whatsAppE164 = formatted;
        }
        else if (phoneRequired) {
            return;
        }
        editMutation.mutate({ profile: myProfile.data, body: { name: trimmedName, whatsAppE164 } }, {
            onSuccess: () => {
                void navigate({ to: '/' });
            },
        });
    };
    return (_jsx(ProfileSetupScreen, { fullName: fullName, isSubmitting: editMutation.isPending, onComplete: onComplete, onFullNameChange: setFullName, onWhatsAppCountryCodeChange: setWhatsappCountryCode, onReturnToLogin: () => {
            void logout.mutateAsync();
        }, isPhoneRequired: phoneRequired, onWhatsAppNumberChange: setWhatsappNumber, whatsappCountryCode: whatsappCountryCode, whatsappNumber: whatsappNumber }));
}
